import { useState, useEffect } from "react";
import { getStagesForOrder, getStageIndex } from "../data/orderStages";
import { generateInvoice, paymentStatus } from "../utils/invoice";
import styles from "./Admin.module.css";

const API_BASE = "https://ratnapura-gems-api.vercel.app/api";

// <input type="date"> needs yyyy-mm-dd, not a full ISO timestamp.
function toDateInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

const EMPTY_ORDER = {
  customerName: "", customerPhone: "", customerEmail: "",
  productName: "", orderNotes: "", hasCertificate: true, createdAt: "",
  itemPrice: "", certificateFee: "", certificateLabel: "Full gem report",
  advancePaid: "",
};

export default function AdminOrders({ password, showToast }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_ORDER);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [prontoInputs, setProntoInputs] = useState({});

  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${password}`,
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/orders`, { headers: authHeaders });
      const data = await r.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      showToast("Couldn't load orders", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.customerName.trim()) {
      showToast("Customer name is required", "error");
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/orders`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Failed");
      showToast(`Order created — tracking number ${data.trackingId}`);
      // Hand the shop the invoice immediately so it can go straight to
      // the customer without a second click.
      try {
        await generateInvoice(data);
      } catch (pdfErr) {
        console.error(pdfErr);
        showToast("Order saved, but the invoice PDF failed to generate", "error");
      }
      setForm(EMPTY_ORDER);
      setShowForm(false);
      loadOrders();
    } catch (err) {
      showToast(err.message || "Couldn't create order", "error");
    }
    setSaving(false);
  };

  const updateOrder = async (trackingId, updates, successMsg) => {
    try {
      const r = await fetch(`${API_BASE}/orders`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({ trackingId, ...updates }),
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.error || "Failed");
      }
      showToast(successMsg);
      loadOrders();
    } catch (err) {
      showToast(err.message || "Update failed", "error");
    }
  };

  const handleDelete = async (trackingId) => {
    if (!window.confirm(`Delete order ${trackingId}? This can't be undone.`)) return;
    try {
      const r = await fetch(`${API_BASE}/orders`, {
        method: "DELETE",
        headers: authHeaders,
        body: JSON.stringify({ trackingId }),
      });
      if (!r.ok) throw new Error("Failed");
      showToast("Order deleted");
      loadOrders();
    } catch {
      showToast("Couldn't delete order", "error");
    }
  };

  const copyLink = (trackingId) => {
    const url = `https://ratnapuragems.lk/track?id=${trackingId}`;
    navigator.clipboard?.writeText(url);
    showToast("Tracking link copied to clipboard");
  };

  const whatsappLink = (order) => {
    const url = `https://ratnapuragems.lk/track?id=${order.trackingId}`;
    const msg = encodeURIComponent(
      `Hello${order.customerName ? " " + order.customerName : ""}! Your Ratnapura Gems order is confirmed.\n\nTracking number: ${order.trackingId}\nTrack your order here: ${url}`
    );
    const phone = (order.customerPhone || "").replace(/\D/g, "");
    const intl = phone.startsWith("94") ? phone : phone.replace(/^0/, "94");
    return `https://wa.me/${intl}?text=${msg}`;
  };

  const filtered = orders.filter((o) => {
    const q = search.toLowerCase();
    return (
      (o.trackingId || "").toLowerCase().includes(q) ||
      (o.customerName || "").toLowerCase().includes(q) ||
      (o.productName || "").toLowerCase().includes(q)
    );
  });

  const activeCount = orders.filter((o) => o.stage !== "shipped").length;

  return (
    <>
      {/* Stats */}
      <div className={styles.stats}>
        {[
          { num: orders.length, label: "Total Orders" },
          { num: activeCount, label: "In Progress" },
          { num: orders.filter((o) => o.stage === "shipped").length, label: "Shipped" },
          { num: orders.filter((o) => o.stage === "ready").length, label: "Ready" },
        ].map((s) => (
          <div key={s.label} className={styles.statCard}>
            <div className={styles.statNum}>{s.num}</div>
            <div className={styles.statLabel}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <input
            type="text"
            placeholder="Search by tracking no, customer or item…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <button className={styles.addBtn} onClick={() => setShowForm(!showForm)}>
          {showForm ? "Cancel" : "+ New Order"}
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className={styles.formCard}>
          <h2 className={styles.formTitle}>Create New Order</h2>
          <form onSubmit={handleCreate} className={styles.form}>
            <div className={styles.formSection}>
              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label>Customer Name *</label>
                  <input
                    value={form.customerName}
                    onChange={(e) => setForm({ ...form, customerName: e.target.value })}
                    placeholder="Anjana Samarakoon"
                    required
                  />
                </div>
                <div className={styles.field}>
                  <label>Phone</label>
                  <input
                    value={form.customerPhone}
                    onChange={(e) => setForm({ ...form, customerPhone: e.target.value })}
                    placeholder="0765851400"
                  />
                </div>
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.customerEmail}
                    onChange={(e) => setForm({ ...form, customerEmail: e.target.value })}
                    placeholder="customer@example.com"
                  />
                </div>
                <div className={styles.field}>
                  <label>Item</label>
                  <input
                    value={form.productName}
                    onChange={(e) => setForm({ ...form, productName: e.target.value })}
                    placeholder="Blue Sapphire Halo Ring"
                  />
                </div>
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label>Order Date <span style={{ fontWeight: 400, color: "#7a6e68" }}>(leave blank for today)</span></label>
                  <input
                    type="date"
                    value={form.createdAt}
                    onChange={(e) => setForm({ ...form, createdAt: e.target.value })}
                  />
                </div>
                <div className={styles.field} />
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label>Item Price (LKR)</label>
                  <input
                    type="number" min="0"
                    value={form.itemPrice}
                    onChange={(e) => setForm({ ...form, itemPrice: e.target.value })}
                    placeholder="23500"
                  />
                </div>
                <div className={styles.field}>
                  <label>Advance Paid (LKR)</label>
                  <input
                    type="number" min="0"
                    value={form.advancePaid}
                    onChange={(e) => setForm({ ...form, advancePaid: e.target.value })}
                    placeholder="11750"
                  />
                </div>
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.field}>
                  <label>Certificate Fee (LKR)</label>
                  <input
                    type="number" min="0"
                    value={form.certificateFee}
                    onChange={(e) => setForm({ ...form, certificateFee: e.target.value })}
                    placeholder="3000"
                  />
                </div>
                <div className={styles.field}>
                  <label>Certificate Label</label>
                  <input
                    value={form.certificateLabel}
                    onChange={(e) => setForm({ ...form, certificateLabel: e.target.value })}
                    placeholder="Full gem report"
                  />
                </div>
              </div>

              {/* Live total preview, so the invoice can be sanity-checked
                  before it's generated */}
              <div className={styles.invoicePreview}>
                <span>Balance due</span>
                <strong>
                  LKR {(
                    (Number(form.itemPrice) || 0) +
                    (Number(form.certificateFee) || 0) -
                    (Number(form.advancePaid) || 0)
                  ).toLocaleString()}
                </strong>
              </div>

              <div className={styles.field}>
                <label>Internal Notes</label>
                <textarea
                  rows={2}
                  value={form.orderNotes}
                  onChange={(e) => setForm({ ...form, orderNotes: e.target.value })}
                  placeholder="Ring size US 7, card certificate, 18kt gold…"
                />
              </div>
              <label className={styles.certToggleRow}>
                <input
                  type="checkbox"
                  checked={form.hasCertificate}
                  onChange={(e) => setForm({ ...form, hasCertificate: e.target.checked })}
                />
                <span>Customer ordered a certificate</span>
              </label>
            </div>
            <div className={styles.formActions}>
              <button type="button" className={styles.cancelBtn} onClick={() => { setForm(EMPTY_ORDER); setShowForm(false); }}>
                Cancel
              </button>
              <button type="submit" className={styles.saveBtn} disabled={saving}>
                {saving ? "Creating…" : "Create Order"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Orders list */}
      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>
            Orders <span className={styles.tableCount}>{filtered.length}</span>
          </h2>
        </div>

        {loading ? (
          <div className={styles.emptyState}><p>Loading orders…</p></div>
        ) : filtered.length === 0 ? (
          <div className={styles.emptyState}>
            <p>{orders.length === 0 ? "No orders yet. Create your first one above." : "No orders match your search."}</p>
          </div>
        ) : (
          <div className={styles.orderList}>
            {filtered.map((order) => {
              const hasCert = order.hasCertificate !== false;
              const stages = getStagesForOrder(hasCert);
              const currentIndex = getStageIndex(order.stage, hasCert);
              return (
                <div key={order.trackingId} className={styles.orderCard}>
                  {/* Header */}
                  <div className={styles.orderHead}>
                    <div>
                      <p className={styles.orderTracking}>{order.trackingId}</p>
                      <p className={styles.orderCustomer}>
                        {order.customerName}
                        {order.productName ? ` · ${order.productName}` : ""}
                      </p>
                      {order.invoiceNumber && (
                        <p className={styles.orderInvoiceMeta}>
                          Invoice #{order.invoiceNumber} · {paymentStatus(order)}
                          {(() => {
                            const bal =
                              Number(order.itemPrice || 0) +
                              Number(order.certificateFee || 0) -
                              Number(order.advancePaid || 0);
                            return bal > 0 ? ` · Balance LKR ${bal.toLocaleString()}` : "";
                          })()}
                        </p>
                      )}
                    </div>
                    <div className={styles.orderHeadActions}>
                      <button
                        className={styles.editBtn}
                        onClick={async () => {
                          try {
                            await generateInvoice(order);
                          } catch {
                            showToast("Couldn't generate the invoice", "error");
                          }
                        }}
                      >
                        Invoice
                      </button>
                      <button className={styles.editBtn} onClick={() => copyLink(order.trackingId)}>Copy Link</button>
                      {order.customerPhone && (
                        <a
                          href={whatsappLink(order)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.editBtn}
                          style={{ textDecoration: "none" }}
                        >
                          WhatsApp
                        </a>
                      )}
                      <button className={styles.deleteBtn} onClick={() => handleDelete(order.trackingId)}>Delete</button>
                    </div>
                  </div>

                  {/* Order date — shown to the customer on the tracking page */}
                  <div className={styles.orderDateRow}>
                    <label className={styles.orderDateLabel}>Order date</label>
                    <input
                      type="date"
                      className={styles.orderDateInput}
                      value={toDateInput(order.createdAt)}
                      onChange={(e) => {
                        if (!e.target.value) return;
                        updateOrder(
                          order.trackingId,
                          { createdAt: e.target.value },
                          "Order date updated"
                        );
                      }}
                    />
                  </div>

                  {/* Certificate toggle — controls whether the
                      certification stage appears for this order */}
                  <label className={styles.certToggleRow}>
                    <input
                      type="checkbox"
                      checked={hasCert}
                      onChange={(e) =>
                        updateOrder(
                          order.trackingId,
                          { hasCertificate: e.target.checked },
                          e.target.checked
                            ? "Certificate stage enabled"
                            : "Certificate stage removed"
                        )
                      }
                    />
                    <span>Customer ordered a certificate</span>
                  </label>

                  {/* Stage stepper */}
                  <div className={styles.stagePicker}>
                    {stages.map((stage, i) => (
                      <button
                        key={stage.key}
                        className={`${styles.stageBtn} ${
                          i < currentIndex ? styles.stageBtnDone : ""
                        } ${i === currentIndex ? styles.stageBtnActive : ""}`}
                        onClick={() =>
                          updateOrder(order.trackingId, { stage: stage.key }, `Moved to "${stage.label}"`)
                        }
                        title={stage.description}
                      >
                        <span className={styles.stageBtnIcon}>{stage.icon}</span>
                        {stage.label}
                      </button>
                    ))}
                  </div>

                  {/* Pronto tracking */}
                  {order.stage === "shipped" && (
                    <div className={styles.prontoRow}>
                      <label className={styles.prontoLabel}>Pronto Tracking No.</label>
                      <input
                        className={styles.prontoInput}
                        placeholder="e.g. 29974824"
                        value={prontoInputs[order.trackingId] ?? order.prontoId ?? ""}
                        onChange={(e) =>
                          setProntoInputs({ ...prontoInputs, [order.trackingId]: e.target.value })
                        }
                      />
                      <button
                        className={styles.editBtn}
                        onClick={() =>
                          updateOrder(
                            order.trackingId,
                            { prontoId: (prontoInputs[order.trackingId] ?? order.prontoId ?? "").trim() },
                            "Courier tracking number saved"
                          )
                        }
                      >
                        Save
                      </button>
                    </div>
                  )}

                  {order.orderNotes && (
                    <p className={styles.orderNotes}>📝 {order.orderNotes}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}